import { paymentAPI } from './api';
import axios from 'axios';

// Helper to create order after payment succeeds
const createOrderAfterPayment = async (paymentResponse, paymentData, idToken) => {
  try {
    const { customerDetails, cartItems, amount, deliveryType, shippingCost, deliveryLocation } = paymentData;

    const orderPayload = {
      items: cartItems.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image: item.image
      })),
      totalPrice: amount,
      shippingCost: shippingCost || 0,
      deliveryType: deliveryType || 'standard', // 'quick', 'express', or 'standard'
      shippingAddress: {
        fullName: customerDetails.name,
        addressLine1: customerDetails.address,
        city: customerDetails.city,
        state: customerDetails.state,
        postalCode: customerDetails.pincode,
        pincode: customerDetails.pincode, // Backend also checks this field for Quick validation
        country: customerDetails.country || 'India',
        phone: customerDetails.phone,
        email: customerDetails.email,
        lat: deliveryLocation?.lat || null,
        lng: deliveryLocation?.lng || null
      },
      paymentMethod: 'razorpay',
      paymentDetails: {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature
      }
    };

    console.log('📦 Creating order with payload:', JSON.stringify(orderPayload, null, 2));

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/orders`,
      orderPayload,
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    console.log('✅ Order created successfully:', response.data);
    return response.data;
  } catch (error) {
    // Extract detailed error message from backend
    const backendError = error.response?.data?.error || error.response?.data?.message;
    const errorMessage = backendError || error.message || 'Order creation failed';
    console.error('❌ Error creating order after payment:', errorMessage);
    console.error('Full error:', error.response?.data || error);

    // Throw with detailed message for user to see
    throw new Error(errorMessage);
  }
};

export const razorpayPayment = {
  // Create Razorpay order
  createOrder: async (amount, currency = 'INR', orderId = null, idToken = null) => {
    try {
      // Use Firebase token directly if provided
      if (idToken) {

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/payments/create-razorpay-order`,
          { amount, currency, orderId },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );

        return response.data;
      }

      const response = await paymentAPI.createRazorpayOrder(amount, currency, orderId);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating Razorpay order:', error.response?.data || error.message);
      throw new Error(error.response?.data?.error || error.message);
    }
  },

  // Verify payment
  verifyPayment: async (orderId, paymentId, signature, idToken = null) => {
    try {
      if (idToken) {

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/payments/verify-razorpay-payment`,
          { orderId, paymentId, signature },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );

        return response.data;
      }

      const response = await paymentAPI.verifyRazorpayPayment(orderId, paymentId, signature);
      return response.data;
    } catch (error) {
      console.error('❌ Error verifying Razorpay payment:', error.response?.data || error.message);
      throw new Error(error.response?.data?.error || error.message);
    }
  },

  // Load Razorpay script
  loadRazorpayScript: () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  },

  // Initialize payment
  initializePayment: async (paymentData) => {
    const { amount, currency = 'INR', orderId, customerDetails, cartItems, onSuccess, onError, idToken } = paymentData;

    try {
      // Create order first with Firebase token

      const orderData = await razorpayPayment.createOrder(amount, currency, orderId, idToken);


      // Load Razorpay script
      const scriptLoaded = await razorpayPayment.loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load Razorpay script');
      }

      // Razorpay options
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID, // Use key from .env
        amount: amount,
        currency: currency,
        name: 'Kalinga Crafts Store',
        description: 'Purchase from Kalinga Crafts Store',
        order_id: orderData.orderId,
        handler: async function (response) {
          // Payment successful

          try {
            // Verify payment signature with Firebase token
            const storedToken = window.paymentIdToken;

            await razorpayPayment.verifyPayment(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature,
              storedToken
            );
            // Clear stored token
            window.paymentIdToken = null;


            // Create order in backend
            const createdOrder = await createOrderAfterPayment(response, paymentData, storedToken);


            if (onSuccess && typeof onSuccess === 'function') {

              onSuccess({ ...response, orderId: createdOrder.orderId });
            } else {
              console.error('❌ onSuccess is not a function:', typeof onSuccess);
            }
          } catch (error) {
            console.error('❌ Payment verification or order creation failed:', error);
            console.error('Error object:', error);
            if (onError && typeof onError === 'function') {

              onError(error);
            } else {
              console.error('❌ onError is not a function:', typeof onError);
            }
          }
        },
        prefill: {
          name: customerDetails.name,
          email: customerDetails.email,
          contact: customerDetails.phone,
        },
        notes: {
          address: customerDetails.address,
        },
        theme: {
          color: '#ff6b35',
        },
        modal: {
          ondismiss: function () {
            if (onError) {
              onError(new Error('Payment cancelled by user'));
            }
          }
        },
        config: {
          display: {
            blocks: {
              banks: {
                name: 'All payment methods',
                instruments: [
                  {
                    method: 'upi'
                  },
                  {
                    method: 'card'
                  },
                  {
                    method: 'netbanking'
                  },
                  {
                    method: 'wallet'
                  }
                ]
              }
            },
            preferences: {
              show_default_blocks: true
            }
          }
        },
        // Policy URLs for compliance
        policy_urls: {
          terms: `${window.location.origin}/terms-of-service`,
          privacy: `${window.location.origin}/privacy-policy`,
          refund: `${window.location.origin}/refund-policy`,
          shipping: `${window.location.origin}/shipping-policy`
        }
      };

      const rzp = new window.Razorpay(options);

      // Store idToken for verification after payment
      window.paymentIdToken = idToken;

      rzp.open();

    } catch (error) {
      console.error('❌ Payment initialization failed:', error.message);
      console.error('Error details:', error);
      if (onError) {
        onError(new Error(error.message || 'Payment initialization failed'));
      }
    }
  },
};

// Wrapper function for direct calling
const razorpayPaymentHandler = async (paymentData) => {
  return razorpayPayment.initializePayment(paymentData);
};

export default razorpayPaymentHandler;
